import React from 'react';
import { PageTransition } from '../components/shared/PageTransition';
import { SEOHelmet } from '../components/seo/SEOHelmet';
import styles from './Privacy.module.css';

export const Privacy: React.FC = () => {

  return (
    <PageTransition>
      <SEOHelmet
        title="Privacy Policy"
        description="Privacy Policy and Cookie Policy for GCBusiness.ltd - Learn how we collect, use, and protect your personal data in compliance with UK GDPR."
        canonical="https://gcbusiness.space/privacy"
      />
      <div className={styles.privacyPage}>
        <div className={styles.container}>
          <h1 className={styles.title}>Privacy Policy – GCBusiness.ltd</h1>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Who We Are</h2>
            <p>
              GCBusiness.ltd is a company incorporated in the United Kingdom with a company number, 16455640.
            </p>
            <p>
              Registered office address at: Level 39, One Canada Square, Canary Wharf, London, England, E14 5AB.
            </p>
            <p>
              Payment services are provided by Gemba Finance Ltd, FCA Safeguarding.
            </p>
            <p>
              Gemba Finance is authorised and regulated by the Financial Conduct Authority (FCA) as an Electronic Money Institution under reference number 804853 for the issuance of electronic money.
            </p>
            <p>
              We are the controller of your personal data under UK GDPR.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>What Personal Data We Collect</h2>
            
            <h3 className={styles.subsectionTitle}>Information you provide:</h3>
            <p>Name, address, email, date of birth, ID documents, employment status, financial data, account activity.</p>

            <h3 className={styles.subsectionTitle}>Automatically collected data:</h3>
            <p>IP address, device/browser type, pages visited, session times. (Via cookies and analytics, see Cookie Policy.)</p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Why We Collect Your Data</h2>
            <p>We process personal data on legal grounds including:</p>
            <ul>
              <li>To deliver services (e.g. account access, transactions).</li>
              <li>Compliance with legal/regulatory obligations (e.g. anti-money-laundering, KYC).</li>
              <li>Fraud prevention and security.</li>
              <li>Where you have given consent—for marketing or optional features.</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Sharing Your Data</h2>
            <p>We may share your data with:</p>
            <ul>
              <li>Third-party service providers supporting operations (e.g. IT, customer support).</li>
              <li>Fraud prevention and credit reference agencies.</li>
              <li>Law enforcement or regulatory bodies when legally required.</li>
              <li>Affiliates and carefully selected partners if you consent.</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>International Transfers</h2>
            <p>
              Any data transferred outside the UK/EU will be protected through appropriate safeguards (e.g. UK adequacy or standard contractual clauses).
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Data Retention</h2>
            <p>We retain your data as long as necessary for contract, legal requirements, fraud monitoring and legitimate business needs.</p>
            <p>Once no longer necessary, we securely delete or anonymise your data.</p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Data Security</h2>
            <p>
              We implement industry-standard technical and organisational measures: encrypted transmission (SSL), firewalls, access controls, employee training.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Your Rights</h2>
            <p>Under UK GDPR you have rights to:</p>
            <p>
              Access, rectification, erasure, restriction of processing, data portability, object to processing, withdraw consent.
            </p>
            <p>Requests are processed within one calendar month once identity is confirmed.</p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>How to Contact Us</h2>
            <p>
              Email us at <a href="mailto:info@gcbusiness.space" className={styles.link}>info@gcbusiness.space</a>, or by post:
            </p>
            <div className={styles.address}>
              <p>Data Protection Officer</p>
              <p>GCBusiness.ltd</p>
              <p>Level 39, One Canada Square</p>
              <p>Canary Wharf, London, England, E14 5AB</p>
              <p>United Kingdom</p>
            </div>
            <p>
              You can lodge a complaint with the Information Commissioner's Office (ICO) if dissatisfied.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Changes to This Policy</h2>
            <p>
              We reserve the right to update this policy. We will publish the effective date and notify users if significant changes occur.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>GDPR Notice (Summary of Key Rights & Legal Basis)</h2>
            
            <h3 className={styles.subsectionTitle}>Lawful Bases for Processing</h3>
            <ul>
              <li>Performance of contract (e.g. providing services)</li>
              <li>Legal and regulatory obligations</li>
              <li>Legitimate interests (e.g. fraud prevention, service improvement)</li>
              <li>Consent (e.g. marketing, analytics cookies)</li>
            </ul>

            <h3 className={styles.subsectionTitle}>Your Rights Under GDPR</h3>
            <p>
              To exercise these rights, contact <a href="mailto:info@gcbusiness.space" className={styles.link}>info@gcbusiness.space</a> or write to our Data Protection Officer.
            </p>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Cookie Policy</h2>
            
            <h3 className={styles.subsectionTitle}>What Are Cookies?</h3>
            <p>
              Cookies are small text files placed on your device by websites to remember your preferences or activity. They may collect personal data like IP address or device info and are used in compliance with GDPR and PECR.
            </p>

            <h3 className={styles.subsectionTitle}>Types of Cookies We Use</h3>
            <ul>
              <li>
                <strong>Strictly necessary cookies:</strong> Essential for secure login, session management, fraud prevention. (You must accept these to use core services.)
              </li>
              <li>
                <strong>Functional cookies:</strong> Remember your preferences (language, display) for improved user experience.
              </li>
              <li>
                <strong>Analytics cookies:</strong> Collect anonymised data on how you navigate services (e.g. Google Analytics, Hotjar) to help us improve our platform.
              </li>
              <li>
                <strong>Targeting/Marketing cookies (if used):</strong> To deliver relevant content and adverts via third-party partners (e.g. Google Ads, LinkedIn) with your consent.
              </li>
            </ul>

            <h3 className={styles.subsectionTitle}>Consent & Control</h3>
            <p>On first visit, you'll be asked to accept or reject optional cookies.</p>
            <p>
              Functional and analytics cookies can be managed or withdrawn at any time via cookie settings—rejecting optional cookies won't prevent access to basic services.
            </p>

            <h3 className={styles.subsectionTitle}>How Long We Keep Cookies</h3>
            <ul>
              <li><strong>Session cookies:</strong> expire after your browser session ends or after inactivity (typically ~10 minutes).</li>
              <li><strong>Persistent (analytics or preferences):</strong> generally expire within 12 months.</li>
            </ul>

            <h3 className={styles.subsectionTitle}>Manage Cookies Via Your Browser</h3>
            <p>
              You can also control or clear cookies using your browser's settings. Rejecting or deleting cookies may affect functionality.
            </p>

            <h3 className={styles.subsectionTitle}>Updates to This Policy</h3>
            <p>
              We may amend this policy from time to time. Any significant changes will be notified and the date updated.
            </p>
          </section>
        </div>
      </div>
    </PageTransition>
  );
};

